"use client";

import { useEffect } from "react";

import { isShellUserAgent } from "@/lib/shell";

/** Publishes the on-screen keyboard's height as `--kb-inset`.
 *
 *  iOS never shrinks the LAYOUT viewport when the keyboard opens — it keeps
 *  the page full-height and scrolls it — so `100svh` still resolves to the
 *  whole screen and any bottom-anchored composer ends up behind the
 *  keyboard, leaving a dead band of whitespace above it. The visual viewport
 *  is the only thing that reflects reality, hence this listener.
 *
 *  Consumers subtract `var(--kb-inset)` from their viewport-height
 *  math (see the scenario player and Preceptor Bot chat containers). The
 *  fallback keeps every non-keyboard context — desktop, and any browser
 *  without the API — completely unaffected.
 *
 *  Covers mobile web and the installed PWA ONLY — it early-returns in the
 *  native shell, which is handled by @capacitor/keyboard's `resize:
 *  "native"` (WKWebView doesn't surface the keyboard through
 *  `visualViewport`; verified by removing the plugin and watching the
 *  whitespace return). The two must not both run: the plugin shrinks the
 *  webview, so a visualViewport delta measured on top of it subtracts the
 *  keyboard a second time.
 */
export function KeyboardInset() {
  useEffect(() => {
    // Never run in the native shell. @capacitor/keyboard's `resize: "native"`
    // already shrank the WKWebView, so `100svh` IS the space above the
    // keyboard — measuring a visualViewport delta on top of that subtracts
    // the keyboard TWICE, leaving the container short and a scrollable band
    // of whitespace below it.
    if (isShellUserAgent(navigator.userAgent)) return;
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    // iOS Safari fires scroll/resize continuously while the URL bar
    // collapses, and --kb-inset sits inside layout-affecting h-[calc(…)] on
    // the two chat containers — so an unconditional write is a document-wide
    // style invalidation per scroll frame. The overwhelmingly common
    // transition is 0px → 0px, so remember the last value and skip.
    let last = 0;
    const update = () => {
      // offsetTop matters: iOS scrolls the visual viewport within the layout
      // viewport, so height alone under-reports how much is actually hidden.
      //
      // `scale > 1` is NOT the keyboard. Nothing disables pinch-zoom (the
      // root viewport export sets only viewportFit), and zooming shrinks
      // visualViewport.height the same way the keyboard does — at 2x this
      // measured "half the screen is hidden" and collapsed both chat
      // containers to a sliver mid-read.
      const hidden =
        viewport.scale > 1
          ? 0
          : window.innerHeight - viewport.height - viewport.offsetTop;
      const next = Math.max(0, Math.round(hidden));
      if (next === last) return;
      last = next;
      root.style.setProperty("--kb-inset", `${next}px`);
    };
    update();
    // Passive: `update` only reads geometry, and iOS fires `scroll` at frame
    // rate while the URL bar collapses — a non-passive listener there blocks
    // the compositor on every scroll of every page (this mounts at the root).
    viewport.addEventListener("resize", update, { passive: true });
    viewport.addEventListener("scroll", update, { passive: true });
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      root.style.removeProperty("--kb-inset");
    };
  }, []);

  return null;
}
